import {
	type Camera,
	type Cartesian2,
	type Cartesian3,
	Cartographic,
	Math as CesiumMath,
	type Ray,
	type Scene,
	SceneMode,
} from 'cesium';

/** Where a click on the map puts a model's anchor. `height` is null when the click says nothing about it. */
export type ClickPlacement = { lon: number; lat: number; height: number | null };

/** `Scene.pickFromRay` is marked private in Cesium (present since 1.50), so it is not in the typings. */
type RayPickingScene = {
	pickFromRay?: (ray: Ray, objectsToExclude?: object[]) => { position?: Cartesian3 } | undefined;
};

/**
 * The point under a click on the map: the rendered surface (tiles or terrain) when depth
 * picking is available, height included, so a model lands on what was clicked. The ray
 * pick looks past `exclude` (the model being placed), which would otherwise put the model
 * on its own roof. Outside 3D there is no such pick, so the surface gives lon/lat only.
 * Without depth picking, the ellipsoid, whose height of 0 sits below the ground, so the
 * caller keeps its own.
 */
export function placementAtClick(
	scene: Scene,
	camera: Camera,
	position: Cartesian2,
	exclude: object[],
): ClickPlacement | null {
	const rayScene = scene as unknown as RayPickingScene;
	const canLookPast = scene.mode === SceneMode.SCENE3D && typeof rayScene.pickFromRay === 'function';
	const ray = canLookPast ? camera.getPickRay(position) : undefined;
	let onSurface: Cartesian3 | undefined;
	if (scene.pickPositionSupported) {
		onSurface = ray ? rayScene.pickFromRay?.(ray, exclude)?.position : scene.pickPosition(position);
	}
	const clicked = onSurface ?? camera.pickEllipsoid(position, scene.globe.ellipsoid);
	if (!clicked) return null;
	const carto = Cartographic.fromCartesian(clicked);
	return {
		lon: CesiumMath.toDegrees(carto.longitude),
		lat: CesiumMath.toDegrees(carto.latitude),
		height: onSurface && ray ? carto.height : null,
	};
}
